import type { SessionWindow } from "./types";
import type { SessionContext } from "./comfort";

const KT = 1.852;
/** Wind below this (km/h ≈ 8kt) is too light for gusts to matter. */
const GUST_WIND_FLOOR_KMH = 15;
/** Gust:mean ratio above which a window reads as gusty. */
const GUST_RATIO = 1.5;

/**
 * Per-window hazard pills, derived from data already on the window's hours and
 * the day-level context. British English, max three, deduplicated. Surf/wind
 * sports do not gate on water quality, so this is the only place they hear it.
 */
export function windowWarnings(w: SessionWindow, ctx: SessionContext): string[] {
  const out: string[] = [];

  let gusty = false;
  let maxGustKt = 0;
  for (const h of w.hours) {
    if (
      h.windGustKmh !== null &&
      h.windSpeedKmh >= GUST_WIND_FLOOR_KMH &&
      h.windGustKmh / h.windSpeedKmh > GUST_RATIO
    ) {
      gusty = true;
      // peak gust among the gusty hours
      maxGustKt = Math.max(maxGustKt, h.windGustKmh / KT);
    }
  }
  if (gusty) out.push(`Gusty — gusting to ${Math.round(maxGustKt)} kt`);

  if (ctx.waterStatus === "poor") out.push("Water flagged poor today");
  else if (ctx.waterStatus === "caution") out.push("Water-quality caution today");

  const usesTodayFallback = w.dayOffset === 0 && w.hours.some((hour) =>
    hour.safetyState === undefined || hour.safetyState === "no-data"
  );
  const usesTodayAmber = w.dayOffset === 0 &&
    ctx.safetyState === "flagged-amber" &&
    w.hours.some((hour) => hour.safetyState === "flagged-amber");
  if (usesTodayFallback || usesTodayAmber) {
    for (const note of ctx.safetyNotes ?? []) {
      const clean = note.trim();
      if (clean && !out.includes(clean)) out.push(clean);
    }
  }

  for (const note of w.hours.flatMap((hour) => hour.safetyNotes ?? [])) {
    const clean = note.trim();
    if (clean && !out.includes(clean)) out.push(clean);
  }

  return out.slice(0, 3);
}
